import { useEffect, useMemo, useRef } from "react";
import type { AreaMapObservationArea } from "@/features/deployment-dashboard/shared/api/area-map-response";
import { useAreaMap } from "@/features/deployment-dashboard/shared/hooks/use-area-map";
import {
  buildFloorplanCacheKey,
  floorplanImageQueryKey,
  type FloorplanImageIdentity,
} from "@/features/deployment-dashboard/shared/floorplan-image-query";
import {
  reshapeMatrix,
  type WorldToPixelTransformAsset,
} from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import { useCachedImageAsset } from "@/shared/api/hooks/use-cached-image-asset";

export type FacilitySchematicAssetsStatus =
  "loading-map" | "no-floorplan" | "loading-image" | "map-error" | "image-error" | "ready";

interface FacilitySchematicAssets {
  status: FacilitySchematicAssetsStatus;
  /** Mounted consumer's object URL for the cache-first floor plan raster, once loaded. */
  floorplanImageUrl: string | undefined;
  floorplanImageSize: { width: number; height: number } | undefined;
  /** Calibrated world-to-pixel transform, once loaded. */
  transform: WorldToPixelTransformAsset | undefined;
  /** Observation area boundaries for this area, still in its world coordinate frame. */
  observationAreas: AreaMapObservationArea[];
  /** Sensor ids configured for this area, for filtering deployment-wide live sensors down to it. */
  sensorIds: string[];
  isLoading: boolean;
  error: unknown;
}

/**
 * Loads the facility schematic's data for a single deployment area - its floor
 * plan raster image, calibrated world-to-pixel transform, observation area
 * boundaries, and configured sensor ids - from the runtime API's
 * `GET /v1/areas/{areaId}/map` (see `useAreaMap`), fetching the floor plan
 * raster itself through the shared cache-first floorplan image query keyed by
 * `deploymentId + floorplanId + revision` (see `floorplan-image-query`), so a
 * selected area whose raster was already background-preloaded resolves
 * immediately instead of re-fetching.
 *
 * Defensively requires the resolved map's own `areaId` to match the requested
 * `areaId` before exposing any of its derived data (transform, observation
 * areas, sensor ids, image), so a stale response for a previously selected
 * area is never briefly presented as the new selection.
 *
 * `status` distinguishes: the map itself loading/erroring, a successfully
 * loaded map with no floor plan configured, the floor plan image
 * loading/erroring, and a fully ready state. A failed image is retried
 * exactly once automatically per newly selected floorplan identity,
 * refreshing the area map first in case its signed URL had gone stale while
 * this area was unselected, then retrying the image fetch with the refreshed
 * URL.
 */
export function useFacilitySchematicAssets(
  deploymentId: string,
  areaId: string | undefined,
  enabled = true
): FacilitySchematicAssets {
  const areaMap = useAreaMap(deploymentId, areaId, enabled);
  const selectedMap = areaId != null && areaMap.data?.areaId === areaId ? areaMap.data : undefined;
  const floorplan = selectedMap?.floorplan ?? undefined;

  const identity: FloorplanImageIdentity | undefined =
    floorplan && selectedMap
      ? { deploymentId, floorplanId: floorplan.floorplanId, revision: floorplan.revision }
      : undefined;
  const cacheKey = identity ? buildFloorplanCacheKey(identity) : undefined;
  const queryKey = identity ? floorplanImageQueryKey(identity) : undefined;
  const image = useCachedImageAsset(deploymentId, floorplan?.imageUrl, { cacheKey, queryKey });

  const identityKey = queryKey?.join(":");
  const retriedIdentityKeyRef = useRef<string | null>(null);
  const areaMapRefetch = areaMap.refetch;
  const imageRefetch = image.refetch;
  const imageError = image.error;

  useEffect(() => {
    if (identityKey == null || !imageError) {
      return;
    }
    if (retriedIdentityKeyRef.current === identityKey) {
      return;
    }
    retriedIdentityKeyRef.current = identityKey;
    // The signed URL may have gone stale while this area/floorplan wasn't
    // selected, so refresh the area map (fetching a fresh signed URL) before
    // retrying the image fetch itself.
    void areaMapRefetch().then(() => imageRefetch());
  }, [identityKey, imageError, areaMapRefetch, imageRefetch]);

  return useMemo<FacilitySchematicAssets>(() => {
    const observationAreas = selectedMap?.observationAreas ?? [];
    const sensorIds = selectedMap?.sensorIds ?? [];
    const transform = floorplan
      ? {
          sourceFrame: floorplan.worldToPixelTransform.sourceFrame,
          targetFrame: floorplan.worldToPixelTransform.targetFrame,
          matrix: reshapeMatrix(floorplan.worldToPixelTransform.matrix),
        }
      : undefined;
    const floorplanImageSize = image.data
      ? { width: image.data.width, height: image.data.height }
      : undefined;

    let status: FacilitySchematicAssetsStatus;
    if (areaMap.error) {
      status = "map-error";
    } else if (!selectedMap) {
      status = "loading-map";
    } else if (selectedMap.floorplan === null) {
      status = "no-floorplan";
    } else if (image.error) {
      status = "image-error";
    } else if (!image.data) {
      status = "loading-image";
    } else {
      status = "ready";
    }

    return {
      status,
      floorplanImageUrl: image.data?.url,
      floorplanImageSize,
      transform,
      observationAreas,
      sensorIds,
      isLoading: status === "loading-map" || status === "loading-image",
      error: areaMap.error ?? image.error ?? null,
    };
  }, [areaMap.error, selectedMap, floorplan, image.data, image.error]);
}
